import { useCallback, useEffect, useMemo, useState } from "react";
import "./MilestonesPage.css";
import { AppShell, SideGroup, SideLink, SideNote } from "../app/AppShell";
import { Button } from "../components/Button";
import { Alert, Card, EmptyState, Loading } from "../components/Card";
import { PageHead } from "../components/Page";
import { Select } from "../components/Field";
import { DashProgress } from "../components/Dash";
import { MilestoneBulkBar, MilestoneForm, MilestoneIssueList } from "../components/Milestones";
import type { Issue, Milestone } from "../lib/tasks";
import { errText, tasksApi } from "../lib/tasks";
import type { Project } from "../lib/project";
import { projectApi } from "../lib/project";
import { useRouteParam } from "../lib/route";
import { tx } from "../i18n";

/* ══ 일감 › 마일스톤(t-in-port-work-milestones) ══
   고르고 → 이슈를 여러 개 체크해 → 한 번에 넣거나 뺀다. 고른 마일스톤은 **주소**(`#/milestones?ms=`)에 산다 —
   레일 링크로 고르고, 공유하면 같은 마일스톤이 열린다. 넣기/빼기는 새 서버 문 없이 `PATCH /tasks/:id` 의
   `milestoneId` 를 고른 이슈마다 부른다(보드 선택 줄과 같은 「N건 됨 · M건 못 함」).
   b3x-pr 의 「사인오프 필요」는 옮기지 않았다 — b3x-in 마일스톤에 그 칸이 없다. */

const msHref = (id: string) => `#/milestones?ms=${encodeURIComponent(id)}`;

export function MilestonesPage() {
  const msId = useRouteParam("ms");
  const [list, setList] = useState<Issue[] | null>(null);
  const [ms, setMs] = useState<Milestone[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [project, setProject] = useState("");
  const [creating, setCreating] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(() => new Set());

  const load = useCallback(async () => {
    try {
      const [ts, m] = await Promise.all([tasksApi.list(), tasksApi.milestones()]);
      setList(ts);
      setMs(m);
      setErr(null);
    } catch (e) {
      setErr(errText(e));
    }
    projectApi.list().then(setProjects, () => undefined);
  }, []);
  useEffect(() => void load(), [load]);
  // 다른 마일스톤으로 갈아타면 고른 것을 비운다 — 안 보이는 고름이 단추에 닿으면 안 된다.
  useEffect(() => setPicked(new Set()), [msId]);

  const shown = useMemo(() => ms.filter((m) => !project || (m.projectId ?? "") === project), [ms, project]);
  const cur = ms.find((m) => m.id === msId) ?? null;

  const side = (
    <>
      <SideGroup>{tx("milestones.rail")}</SideGroup>
      {shown.map((m) => (
        <SideLink key={m.id} href={msHref(m.id)} active={m.id === msId} count={m.taskCount}>
          {m.title}
        </SideLink>
      ))}
      <SideNote>{tx("milestones.railNote", { n: shown.length })}</SideNote>
    </>
  );

  return (
    <AppShell route="/milestones" sidebarTitle={tx("milestones.rail")} sidebar={side}>
      <PageHead title={tx("nav.milestones")} lead={tx("milestones.lead")} />

      <div className="msp__tools">
        <Select size="md" value={project} onChange={(e) => setProject(e.target.value)} aria-label={tx("dashx.scope")}>
          <option value="">{tx("dashx.allProjects")}</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
        {/* 폰은 레일이 서랍이라, 고르는 길을 본문에도 하나 둔다. */}
        <Select
          size="md"
          className="msp__pick"
          value={msId ?? ""}
          onChange={(e) => (location.hash = e.target.value ? msHref(e.target.value) : "#/milestones")}
          aria-label={tx("milestones.pick")}
        >
          <option value="">{tx("milestones.pick")}</option>
          {shown.map((m) => (
            <option key={m.id} value={m.id}>
              {m.title}
            </option>
          ))}
        </Select>
        <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
          + {tx("milestones.new")}
        </Button>
      </div>

      {creating ? (
        <MilestoneForm
          projects={projects}
          defaultProject={project}
          onCancel={() => setCreating(false)}
          onCreated={(m) => {
            setCreating(false);
            void load();
            location.hash = msHref(m.id);
          }}
        />
      ) : null}

      {err ? <Alert tone="error">{tx("milestones.loadFailed", { err })}</Alert> : null}
      {!list && !err ? <Loading text={tx("common.state.loading")} /> : null}

      {list && !cur ? (
        msId ? (
          <Alert tone="warning">{tx("milestones.notFound", { id: msId })}</Alert>
        ) : (
          <EmptyState icon="flag" title={tx("milestones.none")} desc={tx("milestones.noneDesc")} />
        )
      ) : null}

      {list && cur ? (
        <>
          <Card title={cur.title} meta={<span className="tnum">{cur.id}</span>}>
            <div className="msp__info">
              <p className="msp__goal">{cur.goal ? cur.goal : tx("milestones.noGoal")}</p>
              <DashProgress done={cur.doneCount} total={cur.taskCount} label={tx("milestones.progress")} />
            </div>
          </Card>
          <MilestoneIssueList
            ms={cur}
            list={list}
            project={cur.projectId ?? (project || null)}
            picked={picked}
            onPick={(id, on) =>
              setPicked((p) => {
                const n = new Set(p);
                if (on) n.add(id);
                else n.delete(id);
                return n;
              })
            }
          />
          <MilestoneBulkBar
            ms={cur}
            picked={list.filter((t) => picked.has(t.id))}
            onDone={(ok) => {
              setPicked((p) => new Set([...p].filter((id) => !ok.includes(id))));
              void load();
            }}
            onClear={() => setPicked(new Set())}
          />
        </>
      ) : null}
    </AppShell>
  );
}
